import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import { isTestImagesMode } from '@theme/test-images-handler';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    const dialogComponent = document.getElementById('quick-add-dialog');
    if (dialogComponent) {
      dialogComponent.querySelector('.js-quick-add-product-link')?.setAttribute('href', currentUrl);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    let fetchUrl = productPageUrl;

    if (isTestImagesMode()) {
      const url = new URL(fetchUrl);

      url.searchParams.set('test_images', 'true');
      fetchUrl = url.toString();
    }

    try {
      const response = await fetch(fetchUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    const form = productGrid.querySelector('product-form-component form');
    let sellType = 'upsell';

    if (this.closest('collection-results-list') || this.closest('search-results-list')) {
      sellType = 'direct';
    }

    if (form) {
      form.setAttribute('data-sell-type', sellType);
    }

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#applyQuickBuyTestIds(modalContent);
    this.#resolveAnchorLinks(modalContent);
    this.#syncVariantSelection(modalContent);
  }

  /**
   * Rewrites in-page anchor links cloned from the PDP so they navigate to the product page.
   * @param {Element} modalContent - The modal content element
   */
  #resolveAnchorLinks(modalContent) {
    const baseUrl =
      this.productPageUrl ||
      /** @type {HTMLAnchorElement | null} */ (modalContent.querySelector('.view-product-title a'))?.href;

    if (!baseUrl) return;

    for (const link of modalContent.querySelectorAll('.additional-link')) {
      const href = link.getAttribute('href');
      if (!href?.startsWith('#')) continue;

      const url = new URL(baseUrl, window.location.origin);
      url.hash = href;
      link.setAttribute('href', url.toString());
    }
  }

  /**
   * Applies PLP quick-buy data-test-id values to PDP markup cloned into the modal.
   * @param {Element} modalContent - The modal content element
   */
  #applyQuickBuyTestIds(modalContent) {
    const remapTestId = (from, to) => {
      modalContent.querySelectorAll(`[data-test-id="${from}"]`).forEach((element) => {
        element.setAttribute('data-test-id', to);
      });
    };

    remapTestId('ta-pdp-add-to-cart-btn', 'ta-plp-quick-buy-add-to-cart-btn');
    remapTestId('ta-pdp-qty-inc-btn', 'ta-plp-quick-buy-qty-inc-btn');
    remapTestId('ta-pdp-qty-dec-btn', 'ta-plp-quick-buy-qty-dec-btn');

    modalContent.querySelectorAll('[data-test-id^="ta-pdp-color-"]').forEach((element) => {
      const testId = element.getAttribute('data-test-id');
      if (testId) {
        element.setAttribute('data-test-id', testId.replace('ta-pdp-color-', 'ta-plp-quick-buy-color-'));
      }
    });

    modalContent.querySelectorAll('[data-test-id^="ta-pdp-size-"]').forEach((element) => {
      const testId = element.getAttribute('data-test-id');
      if (testId) {
        element.setAttribute('data-test-id', testId.replace('ta-pdp-size-', 'ta-plp-quick-buy-size-'));
      }
    });
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */

  handleCartUpdate = (event) => {
 
    
    if (event.detail?.data?.didError) {
      console.warn('3. Cart error detected, aborting.');
      console.groupEnd();
      return;
    }
    try {
      if (event.detail) {
        event.detail.sourceElement = event.target;

      }
    } catch (e) {
      console.error('3. Failed to mutate event detail (might be frozen):', e);
    }
    console.groupEnd();
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;

    for (const link of this.querySelectorAll('.additional-link')) {
      const href = link.getAttribute('href');
      if (!href) continue;

      const hash = href.startsWith('#') ? href : new URL(href, window.location.origin).hash;
      if (!hash) continue;

      const url = new URL(anchorElement.href, window.location.origin);
      url.hash = hash;
      link.setAttribute('href', url.toString());
    }
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
